import {
    shouldExpandIceFirePageInRoleTree,
    buildOwnedCodeSet
} from '@/constants/permissionRules';

export {
    findTreeNodeByKey,
    collectDescendantKeys,
    resolveModuleCheckedKeys,
    applyToggleFromCheckInfo,
    enforceParentCheckedWithDescendants,
    enforceParentsWhenAllChildrenSelected,
    normalizeModuleCheckedKeys,
    computeTreeCheckState,
    applyParentCascadeToCheckedKeys,
    preparePermissionSelectionForSubmit
} from '@/utils/permissionTreeCodes';

/** 與 PermissionConstants::getPermissionHierarchy 一致 */
export const resolveHierarchyNodeKey = (item) => {
    if (item?.tree_key) {
        return item.tree_key;
    }
    const hasChildren =
        Array.isArray(item?.permissions) && item.permissions.length > 0;
    if (hasChildren && item?.code) {
        return `group-${item.code}`;
    }
    return item?.code ?? null;
};

export const hierarchyArrayToTreeNodes = (arr = []) =>
    (arr || []).map((item) => {
        const nested = Array.isArray(item.permissions)
            ? item.permissions
            : null;
        return {
            key: resolveHierarchyNodeKey(item),
            code: item.code || null,
            title: item.name,
            children: nested?.length
                ? hierarchyArrayToTreeNodes(nested)
                : undefined
        };
    });

export const hierarchyMapToTreeRoots = (obj = {}) =>
    Object.values(obj || {}).map((mod) => ({
        key: mod.code,
        code: mod.code,
        title: mod.name,
        children: Array.isArray(mod.permissions)
            ? hierarchyArrayToTreeNodes(mod.permissions)
            : undefined
    }));

export const buildPermissionTreeFromApi = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return hierarchyArrayToTreeNodes(data);
    if (typeof data === 'object') return hierarchyMapToTreeRoots(data);
    return [];
};

/** 模組手風琴內樹：有子項時略過與標題重複的模組根節點 */
export const treeNodesForModuleRoot = (root) =>
    root?.children?.length ? root.children : root ? [root] : [];

export const flattenTreeKeys = (nodes = [], out = []) => {
    (nodes || []).forEach((node) => {
        if (node?.key != null) out.push(node.key);
        if (node?.children?.length) flattenTreeKeys(node.children, out);
    });
    return out;
};

import {
    buildPermissionKeyToCodeMap,
    collectAssignablePermissionCodes,
    keysToPermissionCodes,
    normalizeModuleCheckedKeys
} from '@/utils/permissionTreeCodes';

export {
    buildPermissionKeyToCodeMap,
    collectAssignablePermissionCodes,
    keysToPermissionCodes
};

/**
 * 角色已擁有的 code → 樹勾選 key（含 170000 super 展開冰火子頁）
 */
/** 舊角色僅有群組碼 171500 時，展開該群組下所有子頁 */
const ICE_FIRE_LEGACY_GROUP_CODES = ['171400', '171500', '171600'];

export const expandOwnedCodesToTreeKeys = (codes = [], treeRoots = []) => {
    const owned = buildOwnedCodeSet(
        (codes || []).map((code) => ({ code: String(code) }))
    );
    const keys = new Set();

    const walk = (nodes = [], activeGroupCode = null) => {
        nodes.forEach((node) => {
            const groupCode =
                node.children?.length && node.code
                    ? node.code
                    : activeGroupCode;
            const code = node.code ? String(node.code) : null;

            if (code && node.key != null) {
                const ownedCode = owned.has(code);
                const superExpand = shouldExpandIceFirePageInRoleTree(
                    code,
                    owned
                );
                const legacyGroupExpand =
                    activeGroupCode &&
                    owned.has(String(activeGroupCode)) &&
                    ICE_FIRE_LEGACY_GROUP_CODES.includes(
                        String(activeGroupCode)
                    );

                if (ownedCode || superExpand || legacyGroupExpand) {
                    keys.add(node.key);
                }
            }

            if (node.children?.length) {
                walk(node.children, groupCode);
            }
        });
    };

    walk(treeRoots);
    return normalizeSelectionWithGroupParents(treeRoots, Array.from(keys));
};

/** 已勾選子頁時補上無 code 的群組父節點（如 group-system） */
export const normalizeSelectionWithGroupParents = (
    treeRoots = [],
    keys = []
) => {
    const merged = new Set(keys);
    (treeRoots || []).forEach((root) => {
        const moduleKeys = new Set(flattenTreeKeys([root]));
        normalizeModuleCheckedKeys(root, [...merged], moduleKeys).forEach((k) =>
            merged.add(k)
        );
    });
    return Array.from(merged);
};
